import {
  BRIDGE_DEFAULT,
  BRIDGE_FRAME,
  CONNECTION_STATUS,
  LINK_LIMITS,
  MQTT_QOS,
  SOCKET_EVENT,
  TOPIC_ROLE,
} from '../catalog/link';
import { isRecord, jsonText, parseJson } from '../../json';
import type { LinkClient, LinkHandlers, LinkMessage } from './types';
import { SUBSCRIBED_ROLES, topicFor, topicSet } from './topics';
import type { TopicSet } from './topics';

/**
 * The bridge gateway as a link client: the same `LinkClient` the broker's own
 * WebSocket listener produces, reached the other way round.
 *
 * A browser cannot open a raw TCP socket, so when the broker's WebSocket
 * listener is out of reach from the tablet's network the only way to the same
 * topics is a process that can. That is this: log in over HTTP, take a JWT, and
 * hold one WebSocket to the gateway, which holds the MQTT connection and
 * multiplexes every attached client onto it.
 *
 * Nothing here speaks MQTT. The frames are the gateway's own JSON protocol
 * (`~/projects/mqtt-bridge`), and the only thing shared with the direct client is
 * the interface — which is the point: the console above cannot tell the two
 * apart except by the wording it is given.
 */

/** The gateway's address and the serial whose topics it serves. */
export interface BridgeSettings {
  /** `http://host:47011`. The `http`/`https` scheme is what the stream rewrites. */
  readonly url: string;
  readonly username: string;
  readonly password: string;
  readonly serial: string;
}

/** One request with a deadline, so a silent gateway cannot hang the guest UI. */
const request = async (url: string, body: unknown, token: string): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LINK_LIMITS.CONNECT_TIMEOUT_MS);
  try {
    return await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token === '' ? {} : { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
};

const tokenFrom = (body: unknown): string => (isRecord(body) ? jsonText(body.token) : '');

/** The token, or the reason there is not one. Never throws: a refusal is a state. */
const login = async (settings: BridgeSettings): Promise<{ token: string; reason: string }> => {
  const endpoint = `${settings.url}${BRIDGE_DEFAULT.LOGIN_PATH}`;
  const credentials = { username: settings.username, password: settings.password };

  let response: Response;
  try {
    response = await request(endpoint, credentials, '');
  } catch {
    return { token: '', reason: `No answer from the bridge at ${settings.url}.` };
  }

  if (!response.ok) {
    return {
      token: '',
      reason: `The bridge refused the login (${response.status}). Check its username and password.`,
    };
  }

  const token = tokenFrom(await response.json().catch(() => null));
  return token === ''
    ? { token: '', reason: 'The bridge accepted the login but issued no token.' }
    : { token, reason: '' };
};

/** The gateway's base URL with its scheme swapped for the WebSocket one. */
export const streamUrl = (base: string, token: string): string => {
  const host = base.replace(/^https?:\/\//, '');
  const scheme = base.startsWith('https://') ? 'wss://' : 'ws://';
  const query = `${BRIDGE_DEFAULT.TOKEN_QUERY}=${encodeURIComponent(token)}`;
  return `${scheme}${host}${BRIDGE_DEFAULT.STREAM_PATH}?${query}`;
};

/** A gateway that answered with a refusal: publishes refuse, messages never arrive. */
const refusedClient = (topics: TopicSet): LinkClient => ({
  topics,
  publish: () => false,
  close: () => undefined,
});

/**
 * Opens the link through the gateway.
 *
 * Rejects only when there is no address to call; a gateway that is down, or that
 * refuses the credentials, comes back as a client which has reported the reason
 * and refuses to publish — so the caller has somewhere else to go.
 */
export const createBridgeClient = async (
  settings: BridgeSettings,
  handlers: LinkHandlers,
): Promise<LinkClient> => {
  if (settings.url.trim() === '') throw new Error('No bridge address configured.');

  const topics = topicSet(settings.serial);

  handlers.onState({
    status: CONNECTION_STATUS.CONNECTING,
    detail: 'Logging in to the bridge…',
  });

  const { token, reason } = await login(settings);
  if (token === '') {
    handlers.onState({ status: CONNECTION_STATUS.ERROR, detail: reason });
    return refusedClient(topics);
  }

  const socket = new WebSocket(streamUrl(settings.url, token));

  socket.addEventListener(SOCKET_EVENT.OPEN, () => {
    /**
     * One frame per role, because a frame carries a single QoS and the log
     * stream wants 0 while the other two want 1. The gateway then subscribes
     * upstream one topic at a time, which is also the only subscribe form
     * mqtt.js serialises correctly.
     */
    SUBSCRIBED_ROLES.forEach((role) => {
      socket.send(
        JSON.stringify({
          type: 'subscribe',
          topics: [topicFor(settings.serial, role)],
          qos: MQTT_QOS[role],
        }),
      );
    });
  });

  socket.addEventListener(SOCKET_EVENT.MESSAGE, (event: MessageEvent<unknown>) => {
    const frame = parseJson(jsonText(event.data));
    if (!isRecord(frame)) return;

    switch (jsonText(frame.type)) {
      case BRIDGE_FRAME.READY:
        handlers.onState({
          status: CONNECTION_STATUS.ONLINE,
          detail: `Listening on ${settings.serial}/… via the bridge.`,
        });
        return;

      case BRIDGE_FRAME.MESSAGE: {
        const message: LinkMessage = {
          topic: jsonText(frame.topic),
          payload: jsonText(frame.payload),
        };
        if (message.topic !== '') handlers.onMessage(message);
        return;
      }

      case BRIDGE_FRAME.ERROR:
        handlers.onState({
          status: CONNECTION_STATUS.ERROR,
          detail: `The bridge refused something: ${jsonText(frame.error)} ${jsonText(frame.message)}`.trim(),
        });
        return;

      default:
        return;
    }
  });

  socket.addEventListener(SOCKET_EVENT.CLOSE, () => {
    handlers.onState({
      status: CONNECTION_STATUS.RECONNECTING,
      detail: 'The bridge connection closed.',
    });
  });

  socket.addEventListener(SOCKET_EVENT.ERROR, () => {
    handlers.onState({
      status: CONNECTION_STATUS.ERROR,
      detail: `The bridge at ${settings.url} could not be reached.`,
    });
  });

  return {
    topics,
    publish: (payload) => {
      if (socket.readyState !== WebSocket.OPEN) return false;
      socket.send(
        JSON.stringify({
          type: 'publish',
          topic: topics.commands,
          payload,
          qos: MQTT_QOS[TOPIC_ROLE.COMMANDS],
          retain: false,
        }),
      );
      return true;
    },
    close: () => socket.close(),
  };
};

/** Whether these settings name a gateway at all, for the cascade to try. */
export const hasBridgeAddress = (settings: BridgeSettings): boolean =>
  settings.url.trim() !== '' && settings.serial.trim() !== '';

/**
 * The origin this app was served from, which is where the gateway is whenever
 * one host serves both — the app, and `/api/*` proxied through to the gateway.
 *
 * It is why no address is compiled in. A quick tunnel's hostname is generated
 * when the process starts, so a baked address goes stale on the next restart
 * and every tablet has to be told about it. Reading the origin instead means the
 * build is portable: the same files work on a tunnel, on a VPS, or on a machine
 * in the hotel, and the address cannot disagree with the page.
 *
 * Empty outside a browser, and on a `file:` page, which is a client with no
 * gateway to find and no route to try.
 */
export const pageOrigin = (): string => {
  if (typeof window === 'undefined') return '';
  const { origin, protocol } = window.location;
  return protocol === 'http:' || protocol === 'https:' ? origin : '';
};
